export type Office = {
  _documentId?: string;
  name: string; // name der praxis
  contactData: string; // Kontakt: tel, email, website, ...
  billingData?: string; // Abrechngunsdaten, Adressse, Bankverbindung, ...
  // connectedUserIds?: string[]; // hier kommen später die userIds der Accounts rein, die mit der Praxis verknpüft sind.
  isCategoryDescriptionsEnabled?: boolean;
  isFullPatientDetailsVisibleEnabled?: boolean; // in der Patientenansicht wird dann der volle Name und Geburtsdatum angezeigt.
};

export type Patient = {
  _documentId?: string;
  uid: string; // uid, die die Praxis frei vergeben kann, darf keine Persönlichkeitsmerkmale enthalten, die den Patienten ausserhalb der Praxis identifizieren könnte, zb. nicht den Nachnamen
  name?: string; // name des Patienten
  gender: string; // m/f
  diagnosis: string; // freitext für Diagnose
  birthYear?: number; // e.g. 2010
  birthMonth?: number; // 1-12
  birthDay?: number; // 1-31
  birthDateIso?: string;
  dominantSide: string; // l/r
  affectedSide: string; // l/r/b links recht beide --> @Todo, die muss änderbar sein, das ist ein historisches Datum. 3 Jahre später kann derselbe Patient zum selben Behandler mit der anderen Seite kommen!
  lastIcfUpdateTime?: string; // isodate-time des letzten Zeitpunktes, an dem der User einen ICF-Score im Bericht gesetzt oder bestätigt hat
};

export type IcfCategory =
  | 'b455'
  | 'b735'
  | 'b750'
  | 'b755'
  | 'b760'
  | 'b770'
  | 'd410'
  | 'd420'
  | 'd430'
  | 'd440'
  | 'd445'
  | 'd450'
  | 'd455'
  | 'd4103'
  | 'd4104'
  | 'd4105'
  | 'd4106'
  | 'd4153'
  | 'd4154'
  | 'd4400'
  | 'd4500'
  | 'd4502'
  | 'd4551'
  | 'e340';

export const icfCategoryLabels: { [key in IcfCategory]: string } = {
  b455: 'Funktionen der kardiorespiratorischen Belastbarkeit',
  b735: 'Funktionen des Muskeltonus',
  b750: 'Funktionen der motorischen Reflexe',
  b755: 'Funktionen der unwillkürlichen Bewegungsreaktionen',
  b760: 'Funktionen der Kontrolle von Willkürbewegungen',
  b770: 'Funktionen der Bewegungsmuster beim Gehen',
  d410: 'Eine elementare Körperposition wechseln',
  d420: 'Sich verlagern',
  d430: 'Gegenstände anheben und tragen',
  d440: 'Feinmotorischer Handgebrauch',
  d445: 'Hand- und Armgebrauch',
  d450: 'Gehen',
  d455: 'Sich auf andere Weise fortbewegen',
  d4103: 'Sich setzen und aufstehen',
  d4104: 'Stehende Position verändern ',
  d4105: 'Sich beugen',
  d4106: 'Seinen Körperschwerpunkt verlagern',
  d4153: 'In sitzender Position verbleiben',
  d4154: 'In stehender Position verbleiben',
  d4400: 'Einen Gegenstand aufheben',
  d4500: 'Kurze Entfernungen gehen',
  d4502: 'Gehen auf unterschiedlichen Unterlagen',
  d4551: 'Klettern / steigen',
  e340: 'Persönliche Hilfs- und Pflegepersonen',
};
export type AssessmentType =
  | 'CUSTOM'
  | 'ARAT'
  | 'BBT'
  | 'ASECS'
  | 'NHPT'
  | 'BBS'
  | 'TS'
  | 'TUG'
  | 'FR'
  | 'MWT6'
  | 'FGA';
export type AssessmentSubType =
  | 'custom'
  | 'nhpt'
  | 'bbt'
  | 'bbs'
  | 'ts'
  | 'fr'
  | 'tug'
  | 'fga'
  | 'mwt6'
  | 'grasp'
  | 'grip'
  | 'pinch'
  | 'gross';

// *****************
// Chart

/**
   *  #752e48
      #773218
      #2d5616
      #005171
      #4d3d7c

      pattern.draw('square', '#ff6384'),
      pattern.draw('circle', '#36a2eb'),
      pattern.draw('diamond', '#cc65fe'),
      pattern.draw('triangle', '#ffce56')

   */

const alpha = '';
export const assessmentTypeColors: { [key in AssessmentType]?: string } = {
  ARAT: '#ff6384' + alpha,
  BBT: '#ce62db' + alpha,
  BBS: '#00b1ad' + alpha,
  NHPT: '#aace56' + alpha,
  // ARAT: '#4d3d7c' + alpha,
  // BBT: '#773218' + alpha,
  // BBS: '#2d5616' + alpha,
  // NHPT: '#005171' + alpha,
};

// *****************
// ChartReport

export type AssessmentChartDataSets = { [key in AssessmentType]?: AssessmentChartDataRow[] };
//export type AssessmentChartDataSetsCustom = AssessmentChartDataSetCustom[];

export type SubTestChartDataSet = {
  subType: AssessmentSubType;
  score: number;
  score_left?: number;
  score_right?: number;
  norm?: number;
  norm_left?: number;
  norm_right?: number;
  sum?: number; // nur für single value Assessments, wo nicht zwischen links und rechts unterschieden wird.
  sumRight?: number;
  sumLeft?: number;
  extraRight?: number; // zusatz value für z.B. nhpt overtime werte
  extraLeft?: number;
  hasComment: boolean;
  comment: string;
};

export type AssessmentChartDataRow = {
  score: number | null;
  score_left?: number;
  score_right?: number;
  subDataSets: Array<SubTestChartDataSet>;
  dateIso: string;
  documentId?: string;
  type: AssessmentType;
  hasComment: boolean;
  comment: string | undefined;
};
export type AssessmentChartDataSetCustom = {
  customAssessment: CustomAssessment;
  assessmentList: Assessment[];
  assessmentChartDataRows: AssessmentChartDataRow[];
};

export type AssessmentChartDataRowMixed = {
  standard?: AssessmentChartDataRow;
  custom?: AssessmentChartDataSetCustom;
};

// *****************
// Report

/**
 * Hier speichern wir Details zu den ICF Kategorien und lagern sie in '/reportnotes'
 */
export type ReportItemNote = {
  _documentId?: string;
  dateIso?: string; // iso zeitpunkt der Speicherung
  icfCategory: IcfCategory;
  note: string;
  icfScoreSelected?: number; // 0-4 oder -1 für 'nicht gewählt'
};

export type Report = ReportItem[];

export type ReportItem = {
  icfCategory: IcfCategory;
  icfScoreSelected?: number; // 0-4 oder -1 für 'nicht gewählt'
  score: number | null; // percent
  scoreAverage?: number | null; // percent über alle tests der icfCategory
  note: string;
  customNote: string;
  details: ReportDetailItem[];
  assessmentTypes: AssessmentType[];
  isAssessmentLaterWarning?: boolean;
};

export type ReportDetailItem = {
  type: AssessmentType;
  subType: AssessmentSubType;
  dateIso: string;
  scorePercent?: number;
  score_left?: number;
  score_right?: number;
  scoreLeftHistory?: number[];
  scoreRightHistory?: number[];
  note?: string;
  history?: ReportDetailItem[];
};
export type ReportDetailItemAggregated = {
  type: AssessmentType;
  aggregatedSubTypeCount: number;
  scorePercentAverage?: number;
};

// ********************
// Data Types

export type SingleNumberDataSet = {
  name: string;
  label?: string;
  value: number | null;
  min: number; // min und max werte für die Eingabe-Daten validerung
  max: number;
};

export type TwoHandsNumberDataSet = {
  name: string;
  label?: string;
  valueLeft: number | null;
  valueRight: number | null;
  min?: number; // min und max werte für die Eingabe-Daten validerung
  max?: number;
};

export type NhptDataSet = {
  name: string;
  label?: string;
  valueLeft: number | null;
  valueRight: number | null;
  min?: number; // min und max werte für die Eingabe-Daten validerung
  max?: number;
  overtimeCountLeft: number | null;
  overtimeCountRight: number | null;
};

export type BbsDataSet = DistinctIcfCategoryDataSet;
export type FgaDataSet = DistinctIcfCategoryDataSet;

export type DistinctIcfCategoryDataSet = {
  name: string;
  label?: string;
  taskInfo?: string;
  value: number | null;
  min: number; // min und max werte für die Eingabe-Daten validerung
  max: number;
  icfCategory: IcfCategory[]; // für BBS und FGA
  score: number | null; // für BBS und FGA
};

export type TsDataSet = {
  name: string;
  label: string; // Muskelgruppe steht dann hier, user-eingabe diesmal.
  left: {
    value: number | null;
    angleV1: number | null;
    angleV3: number | null;
  };
  right: {
    value: number | null;
    angleV1: number | null;
    angleV3: number | null;
  };
  min: number; // min und max werte für die Eingabe-Daten validerung
  max: number; // Scale bis 4 oder bis 5? (könnte sich nochmal ändern, daher im Datensatz hinterlegen)
};

// *****************
// Assessment

// vom User erstellte Assessments (nur der Type mit name etc...)
export type CustomAssessment = {
  _documentId?: string;
  id?: number;
  visible: boolean;
  shortName: string; // Kürzel, muss ein-eindeutig sein.
  fullName?: string;
  icfCategory?: IcfCategory[];
  sideDistinction: boolean;
};

export type CustomData = {
  custom: CustomDataSet<TwoHandsNumberDataSet>;
};

export type CustomFile = {
  url: string;
  name: string;
  type: string;
  createdAt: Date;
  order: number;
};
export type CustomDataSet<DataType> = {
  icfCategory?: IcfCategory[];
  scorePercentage?: number; // für sideDistinction:false
  scorePercentageLeft?: number;
  scorePercentageRight?: number;
  note: string;
  title?: string;
  files?: CustomFile[];
  data: Array<DataType>;
};

export type Assessment = {
  _documentId?: string;
  dateIso: string; // iso zeitpunkt der Testung (Date.toIsoString())
  type: AssessmentType;
  customId?: number; // falls der type custom ist, dann muss hier die id davon stehen.
  score?: number; // beim Arat steht hier der durchscnitt der punkte aus links und rechts
  score_left?: number; // hier steht die summe der punkte links
  score_right?: number;
  scorePercentageLeft?: number;
  scorePercentageRight?: number;
  scorePercentage?: number;
  note?: string;
  data: unknown; // spezifische Erhebungsdaten des jeweiligen Assessments
};

export type SubTestDataSet<DataType> = {
  icfCategory: IcfCategory[];
  score: number | null;
  norm?: number;
  norm_left?: number;
  norm_right?: number;
  score_left?: number;
  score_right?: number;
  scoreLeftPercentage?: number;
  scoreRightPercentage?: number;
  scorePercentage?: number;
  title?: string;
  note: string;
  manual?: string;
  data: Array<DataType>;
};

export type AratData = {
  grasp: SubTestDataSet<TwoHandsNumberDataSet>;
  grip: SubTestDataSet<TwoHandsNumberDataSet>;
  pinch: SubTestDataSet<TwoHandsNumberDataSet>;
  gross: SubTestDataSet<TwoHandsNumberDataSet>;
};
export type BbtData = {
  bbt: SubTestDataSet<TwoHandsNumberDataSet>;
};
export type NhptData = {
  nhpt: SubTestDataSet<NhptDataSet>;
};

export type BbsData = {
  bbs: SubTestDataSet<BbsDataSet>;
};

export type TsData = {
  ts: SubTestDataSet<TsDataSet>;
};

export type FrData = {
  fr: SubTestDataSet<SingleNumberDataSet>;
};

export type TugData = {
  tug: SubTestDataSet<SingleNumberDataSet>;
};

export type Mwt6Data = {
  mwt6: SubTestDataSet<SingleNumberDataSet>;
};

export type FgaData = {
  fga: SubTestDataSet<DistinctIcfCategoryDataSet>;
};

export const initDataCustom: CustomData = {
  custom: {
    title: 'Custom',
    note: '',
    icfCategory: [],
    data: [{ name: 'test1', label: 'Erfüllung in Prozent', valueLeft: null, valueRight: null, min: 0, max: 100 }],
  },
};

export const initDataBBS: BbsData = {
  bbs: {
    icfCategory: [],
    score: null,
    title: 'BBS',
    note: '',
    data: [
      {
        name: 'test1',
        label: '1. Sitz zum Stand',
        taskInfo: `<ul>
<li>4: kann aufstehen ohne die Hände einzusetzen und sich selbstständig stabilisieren</li>
<li>3: kann selbstständig mit Einsatz der Hände aufstehen</li>
<li>2: kann nach einigen Versuchen mit Einsatz der Hände aufstehen</li>
<li>1: braucht minimale Hilfe zum Aufstehen oder zum stabilisieren</li>
<li>0: braucht mäßige bis maximale Hilfe um aufzustehen</li>
</ul>`,
        icfCategory: ['d410'],
        score: null,
        value: null,
        min: 0,
        max: 4,
      },
      {
        name: 'test2',
        label: '2. Stehen ohne Unterstützung',
        taskInfo: `<ul>
<li>4: kann 2 Min. sicher stehen</li>
<li>3: kann 2 Min. unter Supervision stehen</li>
<li>2: kann 30 Sek. ohne Unterstützung stehen</li>
<li>1: braucht einige Versuche, um 30 Sek. Ohne Unterstützung zu Stehen</li>
<li>0: kann nicht ohne Unterstützung 30 Sek. stehen</li>
</ul>`,
        icfCategory: ['d4154'],
        score: null,
        value: null,
        min: 0,
        max: 4,
      },
      {
        name: 'test3',
        label: '3. Sitzen ohne Rückenlehne, beide Füße auf dem Boden',
        taskInfo: `<ul>
<li>4: kann sicher und stabil 2 Min. sitzen</li>
<li>3: kann 2 Min. unter Supervision sitzen</li>
<li>2: kann 30 Sek. sitzen</li>
<li>1: kann 10 Sek. sitzen</li>
<li>0: kann nicht ohne Unterstützung 10 Sek. sitzen</li>
</ul>`,
        icfCategory: ['d4153'],
        score: null,
        value: null,
        min: 0,
        max: 4,
      },
      {
        name: 'test4',
        label: '4. Vom Stehen zum Sitzen',
        taskInfo: `<ul>
<li>4: setzt sich sicher mit minimalem Einsatz der Hände hin</li>
<li>3: kontrolliert das Hinsetzen mit den Händen</li>
<li>2: berührt mit Rückseite der Beine den Stuhl, um das Hinsetzen zu kontrollieren</li>
<li>1: braucht 1 Person zur Hilfestellung</li>
<li>0: braucht 2 Personen zur Hilfestellung oder Supervision um sicher zu sein</li>
</ul>`,
        icfCategory: ['d410'],
        score: null,
        value: null,
        min: 0,
        max: 4,
      },
      {
        name: 'test5',
        label: '5. Transfer',
        taskInfo: `<ul>
<li>4: kann den Transfer sicher mit minimalem Einsatz der Hände auszuführen</li>
<li>3: kann den Transfer sicher ausführen, muss aber die Hände einsetzen</li>
<li>2: kann den Transfer mit verbaler Anweisung und/oder unter Supervision ausführen</li>
<li>1: braucht 1 Person zur Hilfestellung</li>
<li>0: braucht 2 Personen zur Hilfestellung oder Supervision um sicher zu sein</li>
</ul>`,
        icfCategory: ['d420'],
        score: null,
        value: null,
        min: 0,
        max: 4,
      },
      {
        name: 'test6',
        label: '6. Stehen mit geschlossenen Augen ohne Unterstützung',
        taskInfo: `<ul>
<li>4: kann 10 Sek. sicher stehen</li>
<li>3: kann 10 Sek. unter Supervision stehen</li>
<li>2: kann 3 Sek. stehen</li>
<li>1: kann nicht die Augen 3 Sek. geschlossen halten, steht aber stabil</li>
<li>0: braucht Hilfe, um nicht zu fallen</li>
</ul>`,
        icfCategory: ['d4154'],
        score: null,
        value: null,
        min: 0,
        max: 4,
      },
      {
        name: 'test7',
        label: '7. Stehen ohne Unterstützung mit geschlossenen Füßen',
        taskInfo: `<ul>
<li>4: kann selbstständig Füße nebeneinander stellen und 1 Min. sicher stehen</li>
<li>3: kann selbstständig Füße nebeneinander stellen und unter Supervision 1 Min. stehen</li>
<li>2: kann selbstständig Füße nebeneinander stellen und die Position 30 Sek. halten</li>
<li>1: braucht Hilfe um Position einzunehmen, kann aber 15 Sek. mit geschlossenen Füßen stehen</li>
<li>0: braucht Hilfe um die Position einzunehmen, kann diese nicht für 15 Sek. halten</li>
</ul>`,
        icfCategory: ['d4154'],
        score: null,
        value: null,
        min: 0,
        max: 4,
      },
      {
        name: 'test8',
        label: '8. Im Stehen mit ausgestreckten Armen nach vorne reichen/langen',
        taskInfo: `<ul>
<li>4: kann sicher mehr als 25 cm nach vorne langen/reichen</li>
<li>3: kann sicher mehr als 12,5 cm nach vorne reichen/langen</li>
<li>2: kann sicher mehr als 5 cm nach vorne reichen</li>
<li>1: reicht nach vorne braucht aber Supervision</li>
<li>0: verliert das Gleichgewicht beim Versuch/braucht externe Unterstützung</li>
</ul>`,
        icfCategory: ['d4106'],
        score: null,
        value: null,
        min: 0,
        max: 4,
      },
      {
        name: 'test9',
        label: '9. Aus dem Stand Gegenstand vom Boden aufheben',
        taskInfo: `<ul>
<li>4: kann den Schuh sicher und mit Leichtigkeit aufheben</li>
<li>3: kann den Schuh aufheben, braucht aber Supervision</li>
<li>2: kann den Schuh nicht aufheben, reicht aber bis auf 2–5 cm an den Schuh heran und hält selbstständig das Gleichgewicht</li>
<li>1: kann den Schuh nicht aufheben und braucht bei dem Versuch Supervision</li>
<li>0: schon der Versuch scheitert oder braucht Hilfe um das Gleichgewicht nicht zu verlieren bzw. nicht zu fallen</li>
</ul>`,
        icfCategory: ['d4105', 'd4400'],
        score: null,
        value: null,
        min: 0,
        max: 4,
      },
      {
        name: 'test10',
        label: '10. Sicher im Stehen umdrehen, um nach hinten über die rechte und die linke Schulter zu schauen',
        taskInfo: `<ul>
<li>4: schaut hinter sich über beide Seiten bei guter Gewichtsverlagerung</li>
<li>3: schaut nur über eine Seite nach hinten, und zeigt weniger Gewichtsverlagerung auf der anderen Seite</li>
<li>2: dreht sich nur zur Seite aber bewahrt das Gleichgewicht</li>
<li>1: braucht Supervision beim Umdrehen</li>
<li>0: braucht Hilfe um das Gleichgewicht nicht zu verlieren bzw. nicht zu fallen</li>
</ul>`,
        icfCategory: ['d4154', 'd4106'],
        score: null,
        value: null,
        min: 0,
        max: 4,
      },
      {
        name: 'test11',
        label: '11. Sich um 360° drehen',
        taskInfo: `<ul>
<li>4: kann sich um 360° in 4 Sek. oder weniger drehen</li>
<li>3: kann sich nur in einer Richtung sicher um 360° in 4 Sek. oder weniger drehen</li>
<li>2: kann sich sicher um 360° drehen, aber langsam</li>
<li>1: braucht nahe Supervision oder verbale Hilfestellung</li>
<li>0: braucht Hilfe beim Drehen</li>
</ul>`,
        icfCategory: ['d4106'],
        score: null,
        value: null,
        min: 0,
        max: 4,
      },
      {
        name: 'test12',
        label: '12. Ohne Unterstützung abwechselnd die Füße auf eine Stufe stellen',
        taskInfo: `<ul>
<li>4: kann sicher und selbstständig stehen und innerhalb von 20 Sek. die 8 Schrittfolgen bzw. -stufen stellen</li>
<li>3: kann sicher selbstständig stehen und in mehr als 20 Sek. die 8 Schrittfolgen bzw. -Stufen absolvieren</li>
<li>2: kann 4 Schrittfolgen bzw. -stufen ohne Hilfe unter Supervision </li>
<li>1: kann mehr als 2 Schrittfolgen bzw. -stufen mit minimaler Hilfe absolvieren</li>
<li>0: braucht Hilfe um nicht zu fallen oder schon der Versuch scheitert</li>
</ul>`,
        icfCategory: ['d4106'],
        score: null,
        value: null,
        min: 0,
        max: 4,
      },
      {
        name: 'test13',
        label: '13. Tandemstand: Stehen ohne Unterstützung mit einem Fuß vor dem anderen',
        taskInfo: `<ul>
<li>4: kann selbstständig die Füße in den Tandemstand bringen und 30 Sek. Halten</li>
<li>3: kann selbstständig einen Fuß vor den anderen stellen und diese Position 30 Sek. halten</li>
<li>2: kann selbstständig einen kleinen Schritte nach vorne machen und diese Position 30 Sek. halten</li>
<li>1: braucht Hilfe für den Schritt, kann aber Position 15 Sek. beibehalten</li>
<li>0: verliert Gleichgewicht während des Schritts oder des Stehens</li>
</ul>`,
        icfCategory: ['d4154'],
        score: null,
        value: null,
        min: 0,
        max: 4,
      },
      {
        name: 'test14',
        label: '14. Auf einem Bein stehen',
        taskInfo: `<ul>
<li>4: kann ein Bein selbstständig anheben und Position länger als 10 Sek. Halten</li>
<li>3: kann ein Bein selbstständig anheben und Position für 5–10 Sek. Halten</li>
<li>2: kann ein Bein selbstständig anheben und die Position 3 Sek. Oder länger halten</li>
<li>1: versucht ein Bein anzuheben, kann Position nicht 3 Sek. lang beibehalten, bleibt aber selbstständig stehen</li>
<li>0: schon der Versuch scheitert oder Proband braucht Hilfe, um nicht zu fallen</li>
</ul>`,
        icfCategory: ['d4154', 'd4106'],
        score: null,
        value: null,
        min: 0,
        max: 4,
      },
    ],
  },
};

export const initDataBBT: BbtData = {
  bbt: {
    icfCategory: ['d440', 'd445'],
    score: null,
    title: 'BBT',
    note: '',
    manual: `
<h2>Durchführung</h2>
<p>Zuerst wird die nicht betroffene Seite getestet.</p>

<p>Der Patient sitzt mittig vor dem BBT. Beide Hände befinden sich rechts und links der Testbox. Die Seite mit den Würfeln befindet sich auf der Testseite.</p>

<p>Die Zeitmessung beginnt nach dem Kommando „Los“ und endet nach 60 Sekunden. Der Patient muss in dieser Zeit so viele Würfel wie möglich einzeln greifen und zur anderen Kistenseite transportieren.</p>

<p>Jeder Würfel muss mindestens mit den Fingerspitzen über die Trennwand gebracht werden. Danach kann der Patient den Würfel fallen lassen, es ist nicht erforderlich, ihn auf den Boden der Kiste zu legen.</p>

<p>Der Patient wird beim Greifen von zwei Würfeln oder mehr Würfeln nicht unterbrochen. Würfel, welche eventuell aus der Box herausfallen, müssen nicht in die Kiste zurückgelegt werden. Der Patient darf die Würfel während des Tests nicht mischen.</p>

<h2>Testbewertung</h2>

<p>Wenn die Fingerspitzen die Trennwand mit einem Würfel überschreiten, wird der Würfel mit 1 Punkt gezählt. Jede Art des Greifens ist akzeptabel, solange jeweils nur ein Würfel transportiert wird. Wenn die Fingerspitzen die Trennwand nicht überschritten haben, wird der Würfel nicht gezählt (0 Punkte).</p>

<p>Wenn mehr als ein Würfel auf einmal transportiert wurde, wird dies trotzdem nur als 1 Punkt gezählt. Wenn ein Würfel aus der Box gefallen ist, nachdem die Trennwand überquert wurde, wird dieser trotzdem mit 1 Punkt gewertet.</p>
`,
    data: [{ name: 'test1', label: 'Anzahl Blöcke', valueLeft: null, valueRight: null, min: 0, max: 120 }],
  },
};

export const initDataNHPT: NhptData = {
  nhpt: {
    icfCategory: ['d440'],
    score: null,
    title: 'NHPT',
    note: '',
    manual: `
<p>Die Aufgabe des Patienten während der Testung besteht darin, 9 Dübel mit einer Hand einzeln aus der Schale zu nehmen und in die dafür vorgesehenen Löcher auf dem Testbrett zu stecken. Wenn alle Dübel eingesteckt sind, muss der Patient mit derselben Hand die Dübel einzeln wieder in die Schale zurücklegen.</p>

<p>Ausgangsstellung: sitzend auf Stuhl vor einem Tisch, beide Hände des Patienten auf dem Tisch</p>

<p>Der Patient darf das Brett mit einer Hand halten oder es wird eine rutschfeste Unterlage darunter gelegt. Die Seite mit der Schale für die Dübel wird auf die zu testende Seite gedreht, die 9 Dübel liegen in der Schale. Die Testung beginnt immer mit der dominanten Hand des Patienten, prinzipiell werden pro Hand zwei Durchgänge empfohlen. Die Zeitmessung beginnt, sobald die Finger den ersten Dübel berühren und endet, wenn die Finger den letzten zurückgelegten Dübel verlassen.</p>

<p>Zum besseren Verständnis der Aufgabe kann ein Probedurchgang mit der nicht betroffenen Hand durchgeführt werden. Alternativ kann der Untersucher den Test auch vorher demonstrieren.</p>

<p>Nach Ablauf von 60 Sekunden werden die verbleibenden Dübel gezählt und als Punkte in das zusätzliche Feld eingetragen. Jeder Dübel der eingesteckt und jeder Dübel der wieder herausgezogen wurde zählt jeweils 1 Punkt, sodaß maximal 18 Punkte möglich sind.</p>
`,
    data: [
      {
        name: 'test1',
        label: 'Zeitmessung in Sekunden',
        valueLeft: null,
        valueRight: null,
        min: 0,
        max: 60,
        overtimeCountLeft: null,
        overtimeCountRight: null,
      },
      // {
      //   name: 'test2',
      //   label: 'Optional: In 60 Sekunden eingesteckte (9) und dann wieder ausgesteckte (9) Dübel (max: 18)',
      //   valueLeft: null,
      //   valueRight: null,
      // },
    ],
  },
};

export const initDataARAT: AratData = {
  grasp: {
    icfCategory: ['d430', 'd440', 'd445'],
    score: null,
    title: '1. Greifen',
    note: '',
    manual: `
<ul>
  <li>3 Punkte: Wenn der Patient das Objekt richtig gegriffen, platziert und auch losgelassen hat. Wenn der Patient das Objekt nach Erreichen der Endposition loslässt, dieses beim Zurückziehen der Hand aber trotzdem noch umwirft, wird dies <em>nicht</em> mit Punktabzug geahndet.</li>
  <li>2 Punkte: Wenn der Patient das Regal erreicht, er aber das Objekt am Endpunkt nicht loslassen kann.</li>
  <li>1 Punkt: Wenn der Patient den Gegenstand greifen und vom Tisch abheben kann, er aber das Regal nicht erreicht (weil beispielsweise die Schulterfunktion eingeschränkt ist).</li>
  <li>0 Punkte: Wenn der Patient den Gegenstand nicht greifen und anheben kann.</li>
</ul>
`,
    data: [
      { name: 'test1', label: '1. Holzwürfel (10 cm)', valueLeft: null, valueRight: null, min: 0, max: 3 },
      { name: 'test2', label: '2. Holzwürfel (2,5 cm)', valueLeft: null, valueRight: null, min: 0, max: 3 },
      { name: 'test3', label: '3. Holzwürfel (5 cm)', valueLeft: null, valueRight: null, min: 0, max: 3 },
      { name: 'test4', label: '4. Holzwürfel (7,5 cm)', valueLeft: null, valueRight: null, min: 0, max: 3 },
      { name: 'test5', label: '5. Holzkugel (7,5 cm)', valueLeft: null, valueRight: null, min: 0, max: 3 },
      { name: 'test6', label: '6. Stein (10 × 2,5 × 1 cm)', valueLeft: null, valueRight: null, min: 0, max: 3 },
    ],
  },
  grip: {
    icfCategory: ['d430', 'd440', 'd445'],
    score: null,
    title: '2. Griff',
    note: '',
    manual: `
<ul>
  <li>3 Punkte: Der Test wird normal ausgeführt (vollständig und korrekt).</li>
  <li>2 Punkte: Der Patient führt die Aufgabe zwar aus, braucht jedoch eine sehr lange Zeit dazu und hat große Schwierigkeiten dabei. Die Ausführung ist sehr langsam oder sehr ungeschickt.</li>
  <li>1 Punkt: Der Patient führt die Aufgabe nur teilweise aus. Die Stufe wird vergeben, wenn der Patient fähig ist, den Gegenstand hochzuheben.</li>
  <li>0 Punkte: Der Patient ist nicht fähig, irgendwelche Teile der Aufgabe durchzuführen. Es ist ihm nicht einmal möglich, Gegenstände von ihren Plätzen oder auf den Tisch zu schieben.</li>
</ul>`,
    data: [
      { name: 'test1', label: '1. Wasser umgießen', valueLeft: null, valueRight: null, min: 0, max: 3 },
      {
        name: 'test2',
        label: '2. Röhrchen (2,25 × 11,5 cm) platzieren',
        valueLeft: null,
        valueRight: null,
        min: 0,
        max: 3,
      },
      { name: 'test3', label: '3. Röhrchen (1 × 16 cm) platzieren', valueLeft: null, valueRight: null, min: 0, max: 3 },
      {
        name: 'test4',
        label: '4. Unterlegscheibe ergreifen und aufstecken',
        valueLeft: null,
        valueRight: null,
        min: 0,
        max: 3,
      },
    ],
  },
  pinch: {
    icfCategory: ['d430', 'd440', 'd445'],
    score: null,
    title: '3. Präzisionsgriff',
    note: '',
    manual: `
<ul>
  <li>3 Punkte: Wenn der Patient das Objekt richtig gegriffen, platziert und auch losgelassen hat. Wenn der Patient das Objekt nach Erreichen der Endposition loslässt, dieses beim Zurückziehen der Hand aber trotzdem noch umwirft, wird dies <em>nicht</em> mit Punktabzug geahndet.</li>
  <li>2 Punkte: Wenn der Patient das Regal erreicht, er aber das Objekt am Endpunkt nicht loslassen kann.</li>
  <li>1 Punkt: Wenn der Patient den Gegenstand greifen und vom Tisch abheben kann, er aber das Regal nicht erreicht (weil beispielsweise die Schulterfunktion eingeschränkt ist).</li>
  <li>0 Punkte: Wenn der Patient den Gegenstand nicht greifen und anheben kann.</li>
</ul>`,
    data: [
      {
        name: 'test1',
        label: '1. kleine Kugel (6 mm) mit Daumen und Ringfinger',
        valueLeft: null,
        valueRight: null,
        min: 0,
        max: 3,
      },
      {
        name: 'test2',
        label: '2. Murmel (1,5 cm) mit Daumen und Zeigefinger',
        valueLeft: null,
        valueRight: null,
        min: 0,
        max: 3,
      },
      {
        name: 'test3',
        label: '3. kleine Kugel (6 mm) mit Daumen und Mittelfinger',
        valueLeft: null,
        valueRight: null,
        min: 0,
        max: 3,
      },
      {
        name: 'test4',
        label: '4. kleine Kugel (6 mm) mit Daumen und Zeigefinger',
        valueLeft: null,
        valueRight: null,
        min: 0,
        max: 3,
      },
      {
        name: 'test5',
        label: '5. Murmel (1,5 cm) mit Daumen und Ringfinger',
        valueLeft: null,
        valueRight: null,
        min: 0,
        max: 3,
      },
      {
        name: 'test6',
        label: '6. Murmel (1,5 cm) mit Daumen und Mittelfinger',
        valueLeft: null,
        valueRight: null,
        min: 0,
        max: 3,
      },
    ],
  },
  gross: {
    icfCategory: ['b760'],
    score: null,
    title: '4. Basale Armbewegung',
    note: '',
    manual: `
<ul>
  <li>3 Punkte: Leicht ausgleichende Bewegungen mit dem Körper sind während der Aufgaben erlaubt.</li>
  <li>2 Punkte (bei Aufgabe 1): Wenn der Kopf erst dann nach vorn gebeugt wird, nachdem die Verlängerung des Nackens erreicht wurde, um nunmehr die korrekte Endposition einzunehmen.</li>
  <li>1 Punkt: Wenn der Patient seinen Kopf nach vorn beugt, um die Ausführung zu erleichtern, denn dann ist die Aufgabe nicht komplett gelöst.</li>
  <li>0 Punkte: Wenn der Patient keinen Teil der Aufgabe durchführen kann.</li>
</ul>`,
    data: [
      { name: 'test1', label: '1. Hand an den Hinterkopf bringen', valueLeft: null, valueRight: null, min: 0, max: 3 },
      { name: 'test2', label: '2. Hand auf den Kopf bringen', valueLeft: null, valueRight: null, min: 0, max: 3 },
      { name: 'test3', label: '3. Hand zum Mund bringen', valueLeft: null, valueRight: null, min: 0, max: 3 },
    ],
  },
};

export const initDataTS: TsData = {
  ts: {
    icfCategory: ['b735', 'b750'],
    score: null,
    title: 'TS',
    note: '',
    data: [
      {
        name: 'test1',
        label: 'Muskelgruppe 1',
        left: {
          value: null,
          angleV1: null,
          angleV3: null,
        },
        right: {
          value: null,
          angleV1: null,
          angleV3: null,
        },
        min: 0,
        max: 5,
      },
      // n Mal -> User kann beliebig viele Tests hinzufügen.
    ],
  },
};

export const initDataFR: FrData = {
  fr: {
    icfCategory: ['b755', 'd4106'],
    score: null,
    title: 'FR',
    note: '',
    data: [{ name: 'test1', label: 'Entfernung in cm', value: null, min: 0, max: 150 }],
  },
};

export const initDataTug: TugData = {
  tug: {
    icfCategory: ['d410', 'd4103', 'd4104', 'd4500'],
    score: null,
    title: 'TUG',
    note: '',
    manual: `
<p>
Beim TUG sitzt der Patient in einem Stuhl, geht 3 Meter, kehrt zum Stuhl zurück und setzt sich hin. Es wird die benötigte Zeit gemessen. Ab einer Zeit von 20 Sekunden kann ein höheres Fallrisiko bestehen. 
</p>`,
    data: [{ name: 'test1', label: 'Dauer in Sekunden', value: null, min: 0, max: 60 }],
  },
};

export const initDataMWT6: Mwt6Data = {
  mwt6: {
    icfCategory: ['b455', 'b770', 'd450'],
    score: null,
    title: '6MWT',
    note: '',
    data: [{ name: 'test1', label: 'Entfernung in m', value: null, min: 0, max: 150 }],
  },
};

export const initDataFGA: FgaData = {
  fga: {
    icfCategory: [],
    score: null,
    title: 'FGA',
    manual: `
    <p>Beim FGA durchläuft der Patient einige Aufgaben, die mit einem Score von 0 - 3 bewertet werden.</p>
    <ul>
        <li>3 Punkte: Normal</li>
        <li>2 Punkte: Leichte Schädigung</li>
        <li>1 Punkte: Mäßige Schädigung</li>
        <li>0 Punkte: Schwere Schädigung</li>
    </ul>`,
    note: '',
    data: [
      {
        name: 'test1',
        label: '1. Gehen auf einer Ebene',
        taskInfo: ``,
        icfCategory: ['d450'],
        score: null,
        value: null,
        min: 0,
        max: 3,
      },
      {
        name: 'test2',
        label: '2. Wechseln der Geschwindigkeit',
        taskInfo: ``,
        icfCategory: ['d450'],
        score: null,
        value: null,
        min: 0,
        max: 3,
      },
      {
        name: 'test3',
        label: '3. Gang mit horizontaler Kopfdrehung',
        taskInfo: ``,
        icfCategory: ['d450'],
        score: null,
        value: null,
        min: 0,
        max: 3,
      },
      {
        name: 'test4',
        label: '4. Gang mit vertikaler Kopfdrehung',
        taskInfo: ``,
        icfCategory: ['d450'],
        score: null,
        value: null,
        min: 0,
        max: 3,
      },
      {
        name: 'test5',
        label: '5. Gang und Drehung um 180°',
        taskInfo: ``,
        icfCategory: ['d450'],
        score: null,
        value: null,
        min: 0,
        max: 3,
      },
      {
        name: 'test6',
        label: '6. Übersteigen eines Hindernisses',
        taskInfo: ``,
        icfCategory: ['d4502'],
        score: null,
        value: null,
        min: 0,
        max: 3,
      },
      {
        name: 'test7',
        label: '7. Gehen mit schmaler Unterstützungsfläche',
        taskInfo: ``,
        icfCategory: ['d455'],
        score: null,
        value: null,
        min: 0,
        max: 3,
      },
      {
        name: 'test8',
        label: '8. Gang mit geschlossenen Augen',
        taskInfo: ``,
        icfCategory: ['d455'],
        score: null,
        value: null,
        min: 0,
        max: 3,
      },
      {
        name: 'test9',
        label: '9. Rückwärtsgehen',
        taskInfo: ``,
        icfCategory: ['d455'],
        score: null,
        value: null,
        min: 0,
        max: 3,
      },
      {
        name: 'test10',
        label: '10. Treppe',
        taskInfo: ``,
        icfCategory: ['d4551'],
        score: null,
        value: null,
        min: 0,
        max: 3,
      },
    ],
  },
};
